<template>
  <LcLotteryTeaser
    v-bind="{
      backgroundPattern,
      badge,
      drawDate,
      headline,
      jackpot,
      primaryButtonProps,
      secondaryButtonProps,
      subline,
    }"
    :aria-label="id"
    :cutoff-time="closingDate"
    :draw-date-headline="$t('lottery.teaser.drawdate')"
    :style="theme"
    :variant="lotteryTeaserVariant"
    @outdated="emit('update-teasers')"
    @primary-button-click="addBonusCode"
    @secondary-button-click="addBonusCode"
    @teaser-click="addBonusCode"
  >
    <template #lottery-logo>
      <LcLotteryLogo v-bind="{ licensedTerritory, lotteryId }" size="fluid" />
    </template>

    <template v-if="isPickLottery" #secondary-button>
      <PickLotteryQuickPick v-bind="{ lotteryId }" @confirm="onQuickPickModalConfirm" />
    </template>
  </LcLotteryTeaser>
</template>

<script lang="ts" setup>
import type { LcLotteryTeaserVariant } from '@lottocom/frontend-components'

import type {
  CampaignPayload,
  JackpotType,
  LotteryId,
  Money,
  QuickPickConfiguration,
} from '#api/shared/@types/generated/backend/graphql-schema-types'

import {
  QUERY_QUICK_PICK,
  QUERY_QUICK_PICK_BET_TYPE,
  QUERY_QUICK_PICK_NUMBERS,
  QUERY_QUICK_PICK_WAGER,
} from '~/constants/queryParams'

import type { PickLotteryQuickPickModalConfirmPayload } from './PickLotteryQuickPickModal.vue'

export type LotteryTeaserProps = {
  campaign?: Maybe<CampaignPayload>
  closingDate: string
  comingSoon: boolean
  drawDate: string
  id: string
  jackpotSize: Money
  jackpotType: JackpotType
  licensedTerritory: string
  quickPick?: Maybe<Pick<QuickPickConfiguration, 'plays'>>
  variant?: LcLotteryTeaserVariant
}

type LotteryTeaserEmit = {
  (e: 'update-teasers'): void
}

const emit = defineEmits<LotteryTeaserEmit>()
const {
  campaign,
  comingSoon,
  id,
  jackpotSize,
  jackpotType,
  licensedTerritory,
  quickPick,
  variant,
} = defineProps<LotteryTeaserProps>()

const { getTranslation, t } = useI18nUtils()
const { navigateToLocalized } = useRouterUtils()

const route = useRoute()

const headline = computed(() => {
  if (subline.value) return

  return jackpotType === 'FIXED' ? t('lottery.teaser.headline.fixed') : t('lottery.teaser.headline')
})

const jackpot = computed(() => ({
  ...jackpotSize,
  placeholder: comingSoon ? t('lottery.teaser.jackpot.placeholder') : undefined,
}))

const lotteryId = computed(() => id as LotteryId)

const primaryButtonProps = computed(() => ({
  ariaLabel: t('lottery.teaser.button.playnow.arialabel', {
    lottery: t(`lottery.name.${id}`),
  }),
  text: t('lottery.teaser.button.playnow'),
  to: {
    path: `/${id.toLowerCase()}`,
  },
}))

const secondaryButtonProps = computed(() => {
  // TODO: Remove variant check if SF-4966 has been released
  if (!quickPick || variant === 'expressive') return
  return {
    ariaLabel: t('lottery.teaser.button.quickpick.arialabel', {
      count: quickPick.plays,
      lottery: t(`lottery.name.${id}`),
    }),
    text: t('lottery.quickpick.button', quickPick.plays),
    to: {
      path: `/${id.toLowerCase()}/options`,
      query: {
        quickpick: 'true',
      },
    },
  }
})

const subline = computed(
  () =>
    getTranslation({
      key: `lottery.teaser.subline.${id}.${licensedTerritory}`,
    }) ||
    getTranslation({
      key: `lottery.teaser.subline.${id}`,
    }),
)

const theme = computed(() => {
  const { createCssVar } = getLotteryCssVarConstructor(licensedTerritory, lotteryId.value)

  const primaryColor = createCssVar('color-primary')
  return {
    '--lottery-jackpot-color': primaryColor,
    '--lottery-teaser-frame-color': primaryColor,
  }
})

const { isPickLottery } = useLotteryType(lotteryId.value)
const { addBonusCode, backgroundPattern, badge, lotteryTeaserVariant } = useOnsiteMarketingTeaser(
  toRef(() => campaign),
  toRef(() => variant),
)

const onQuickPickModalConfirm = async ({
  betType,
  numbers,
  quantity,
  wager,
}: PickLotteryQuickPickModalConfirmPayload) => {
  addBonusCode()
  await navigateToLocalized({
    path: `/${id.toLowerCase()}/options`,
    query: {
      [QUERY_QUICK_PICK]: quantity,
      [QUERY_QUICK_PICK_BET_TYPE]: betType,
      [QUERY_QUICK_PICK_NUMBERS]: numbers,
      [QUERY_QUICK_PICK_WAGER]: wager,
    },
  })
}
</script>
